import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SocialProofToast — slim bottom-left popup cycling through real recent
 * purchases (first name + city + product) every ~25s. Sourced from
 * /api/social/recent-purchases (last 14 days, anonymised).
 *
 * Design contract:
 *   - Gracefully renders NOTHING when the API returns []. A starter store with
 *     no recent sales never sees a "ghost town" toast.
 *   - Dismissable via close button. Dismissal persists in sessionStorage so it
 *     doesn't nag a buyer mid-flow.
 *   - Suppressed inside auth / checkout pages (only mounted on the landing).
 */
const ROTATION_MS = 25_000;
const VISIBLE_MS = 9_000;
const STORAGE_KEY = "am_proof_dismissed";

const timeAgo = (iso) => {
  if (!iso) return "just now";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  } catch {
    return "recently";
  }
};

const SocialProofToast = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const rotateTimer = useRef(null);
  const hideTimer = useRef(null);

  // Fetch real recent purchases once on mount.
  useEffect(() => {
    if (dismissed) return;
    api
      .get("/social/recent-purchases", { params: { limit: 8 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [dismissed]);

  // Rotation: show toast, hide after VISIBLE_MS, rotate every ROTATION_MS.
  useEffect(() => {
    if (dismissed || items.length === 0) return;
    let alive = true;
    const showNext = (nextIdx) => {
      if (!alive) return;
      setIdx(nextIdx);
      setVisible(true);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => { if (alive) setVisible(false); }, VISIBLE_MS);
    };
    // First toast appears ~3s after mount so it doesn't compete with hero.
    const firstShow = setTimeout(() => showNext(0), 3000);
    rotateTimer.current = setInterval(() => {
      setIdx((curr) => {
        const next = (curr + 1) % items.length;
        showNext(next);
        return next;
      });
    }, ROTATION_MS);
    return () => {
      alive = false;
      clearTimeout(firstShow);
      clearInterval(rotateTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, [items, dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  if (dismissed || items.length === 0) return null;
  const item = items[idx] || items[0];

  return (
    <AnimatePresence>
      {visible && item && (
        <motion.div
          key={`proof-${idx}`}
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed left-3 sm:left-5 bottom-3 sm:bottom-5 z-40 max-w-[320px] rounded-xl bg-white border border-black/10 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)] overflow-hidden"
          data-testid="social-proof-toast"
          role="status"
          aria-live="polite"
        >
          <Link
            to={`/listing/${item.listing_id}`}
            className="flex items-center gap-3 p-2.5 pr-9 hover:bg-[#FAF8F5] transition-colors"
            onClick={() => setVisible(false)}
            data-testid="social-proof-toast-link"
          >
            <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-[#F3EFE9] flex items-center justify-center">
              {item.listing_image ? (
                <img src={item.listing_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <ShoppingBag className="w-5 h-5 text-[#C05A3C]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] text-muted-foreground font-sans-ui leading-tight">
                <span className="font-semibold text-[#1A2024]">{item.buyer_first_name}</span>
                {item.buyer_city ? ` in ${item.buyer_city}` : ""} just bought
              </div>
              <div
                className="text-sm font-display leading-snug text-[#1A2024] truncate"
                data-testid="social-proof-toast-title"
                title={item.listing_title}
              >
                {item.listing_title}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#C05A3C] mt-0.5">
                {timeAgo(item.paid_at)}
              </div>
            </div>
          </Link>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDismiss(); }}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full hover:bg-black/5 flex items-center justify-center text-muted-foreground"
            aria-label="Dismiss"
            data-testid="social-proof-toast-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocialProofToast;
